import React, { useState, useEffect } from "react";
import { clientApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiUsers,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiMail,
  FiPhone,
  FiMapPin,
  FiFileText,
  FiList,
  FiArrowLeft,
  FiCheckCircle,
  FiUser,
  FiRotateCcw,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../components/Modal";

const ClientsPage = () => {
  const { isAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Backend Search & Pagination states
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  // View state: 'list' (Directory table) | 'form' (Add or Edit Client page)
  const [activeTab, setActiveTab] = useState("list");
  const [editingClient, setEditingClient] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, client: null });

  const initialForm = {
    company_name: "",
    email: "",
    contact: "",
    address: "",
    contact_person_name: "",
    ntn: "",
    status: "active",
  };
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  // Debounce search input by 350ms to query backend efficiently
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchClients = async (currentPage = page, currentLimit = pageSize, currentSearch = debouncedSearch) => {
    try {
      setLoading(true);
      const res = await clientApi.getAll({
        page: currentPage,
        limit: currentLimit,
        search: currentSearch.trim() || undefined,
      });
      setClients(res.data || []);
      if (res.pagination) {
        setPagination(res.pagination);
      } else {
        const total = (res.data || []).length;
        setPagination({
          total,
          page: currentPage,
          limit: currentLimit,
          totalPages: Math.ceil(total / currentLimit) || 1,
        });
      }
    } catch (err) {
      toast.error(err.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients(page, pageSize, debouncedSearch);
  }, [page, pageSize, debouncedSearch]);

  const handleStartAdd = () => {
    setEditingClient(null);
    setFormData(initialForm);
    setActiveTab("form");
  };

  const handleStartEdit = (c) => {
    setEditingClient(c);
    setFormData({
      company_name: c.company_name || "",
      email: c.email || "",
      contact: c.contact || "",
      address: c.address || "",
      contact_person_name: c.contact_person_name || "",
      ntn: c.ntn || "",
      status: c.status || "active",
    });
    setActiveTab("form");
  };

  const handleBackToList = () => {
    setActiveTab("list");
    setEditingClient(null);
  };

  const handleSaveClient = async (e) => {
    e.preventDefault();
    if (!formData.company_name.trim()) {
      toast.warning("Client / Company name is required");
      return;
    }

    try {
      setSubmitting(true);
      if (editingClient) {
        await clientApi.update(editingClient.id, formData);
        toast.success("Client updated successfully");
      } else {
        await clientApi.create(formData);
        toast.success("Client added successfully");
      }
      setActiveTab("list");
      setEditingClient(null);
      fetchClients(page, pageSize, debouncedSearch);
    } catch (err) {
      toast.error(err.message || "Failed to save client");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteModal.client) return;
    try {
      await clientApi.delete(deleteModal.client.id);
      toast.success("Client deleted successfully");
      setDeleteModal({ open: false, client: null });
      fetchClients(page, pageSize, debouncedSearch);
    } catch (err) {
      toast.error(err.message || "Failed to delete client");
    }
  };

  return (
    <div className="fade-in">
      {/* Top Header & Page Navigation Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)" }}>
            Client Management <span style={{ fontSize: "0.85rem", color: "var(--accent)", fontWeight: 700 }}>Customers</span>
          </h2>

        </div>

        {/* View Mode Toggle: Directory vs Add/Edit on Page */}
        <div
          style={{
            display: "flex",
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            gap: 4,
          }}
        >
          <button
            onClick={() => {
              setActiveTab("list");
              setEditingClient(null);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              backgroundColor: activeTab === "list" ? "var(--primary)" : "transparent",
              color: activeTab === "list" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <FiList size={16} />
            Client Directory
          </button>

          <button
            onClick={handleStartAdd}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              backgroundColor: activeTab === "form" ? "var(--primary)" : "transparent",
              color: activeTab === "form" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {editingClient ? <FiEdit2 size={16} /> : <FiPlus size={16} />}
            {editingClient ? `Edit: ${editingClient.company_name}` : "Add Client"}
          </button>
        </div>
      </div>

      {/* ===================== VIEW 1: FORM ON PAGE ===================== */}
      {activeTab === "form" && (
        <div className="fade-in">
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
              marginBottom: 30,
            }}
          >
            {/* Form Top Title Bar */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid var(--slate-100)",
                backgroundColor: "var(--slate-50)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: "var(--radius-md)",
                    backgroundColor: "#EEF2FF",
                    color: "#4F46E5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiUsers size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: "1.18rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                    {editingClient ? `Edit Client: ${editingClient.company_name}` : "Register New Client"}
                  </h3>
                  <p style={{ fontSize: "0.82rem", color: "var(--slate-500)", margin: "3px 0 0 0" }}>
                    {editingClient
                      ? "Update client organization details, focal representative, or delivery addresses."
                      : "Fill in the company details to register a new commercial customer account."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleBackToList}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <FiArrowLeft size={16} /> Back to Directory
              </button>
            </div>

            {/* Form Inputs Container */}
            <form onSubmit={handleSaveClient} style={{ padding: "26px 28px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: 24,
                  marginBottom: 24,
                }}
              >
                {/* Section 1: Client Organization */}
                <div
                  style={{
                    backgroundColor: "var(--slate-50)",
                    border: "1px solid var(--slate-200)",
                    borderRadius: "var(--radius-md)",
                    padding: 20,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      color: "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                      borderBottom: "1px solid var(--slate-200)",
                      paddingBottom: 8,
                    }}
                  >
                    <FiUsers size={16} /> 1. Client Organization Details
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Client / Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Apex Corporation or BlueSky Logistics"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Focal / Contact Person Name
                    </label>
                    <div style={{ position: "relative" }}>
                      <FiUser
                        size={16}
                        style={{
                          position: "absolute",
                          left: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--slate-400)",
                        }}
                      />
                      <input
                        type="text"
                        placeholder="e.g. Michael Chang (Procurement Officer)"
                        value={formData.contact_person_name}
                        onChange={(e) => setFormData({ ...formData, contact_person_name: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 14px 10px 38px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Account Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        backgroundColor: "#fff",
                        fontWeight: 600,
                      }}
                    >
                      <option value="active">Active Client</option>
                      <option value="inactive">Inactive / Inactive Account</option>
                    </select>
                  </div>
                </div>

                {/* Section 2: Contact & Tax Information */}
                <div
                  style={{
                    backgroundColor: "var(--slate-50)",
                    border: "1px solid var(--slate-200)",
                    borderRadius: "var(--radius-md)",
                    padding: 20,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      color: "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                      borderBottom: "1px solid var(--slate-200)",
                      paddingBottom: 8,
                    }}
                  >
                    <FiMail size={16} /> 2. Communication & Registered Location
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "var(--slate-700)",
                          marginBottom: 6,
                        }}
                      >
                        Email Address
                      </label>
                      <div style={{ position: "relative" }}>
                        <FiMail
                          size={15}
                          style={{
                            position: "absolute",
                            left: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "var(--slate-400)",
                          }}
                        />
                        <input
                          type="email"
                          placeholder="contact@client.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          style={{
                            width: "100%",
                            padding: "10px 12px 10px 36px",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--slate-300)",
                            fontSize: "0.9rem",
                            backgroundColor: "#fff",
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.82rem",
                          fontWeight: 700,
                          color: "var(--slate-700)",
                          marginBottom: 6,
                        }}
                      >
                        Contact Number
                      </label>
                      <div style={{ position: "relative" }}>
                        <FiPhone
                          size={15}
                          style={{
                            position: "absolute",
                            left: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "var(--slate-400)",
                          }}
                        />
                        <input
                          type="text"
                          placeholder="+92 300 1234567"
                          value={formData.contact}
                          onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                          style={{
                            width: "100%",
                            padding: "10px 12px 10px 36px",
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--slate-300)",
                            fontSize: "0.9rem",
                            backgroundColor: "#fff",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      NTN (National Tax Number)
                    </label>
                    <div style={{ position: "relative" }}>
                      <FiFileText
                        size={15}
                        style={{
                          position: "absolute",
                          left: 12,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "var(--slate-400)",
                        }}
                      />
                      <input
                        type="text"
                        placeholder="e.g. 7654321-0"
                        value={formData.ntn}
                        onChange={(e) => setFormData({ ...formData, ntn: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 12px 10px 36px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: "var(--slate-700)",
                        marginBottom: 6,
                      }}
                    >
                      Office / Delivery Destination Address
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Street address, building, floor, city..."
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        resize: "vertical",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  paddingTop: 18,
                  borderTop: "1px solid var(--slate-200)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setFormData(initialForm)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "10px 18px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    color: "var(--slate-600)",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.88rem",
                  }}
                >
                  <FiRotateCcw size={15} /> Reset Form
                </button>

                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    type="button"
                    onClick={handleBackToList}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "var(--radius-md)",
                      border: "1px solid var(--slate-300)",
                      backgroundColor: "#fff",
                      color: "var(--slate-700)",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "0.88rem",
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 26px",
                      borderRadius: "var(--radius-md)",
                      border: "none",
                      backgroundColor: "var(--primary)",
                      color: "#fff",
                      cursor: submitting ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      fontSize: "0.92rem",
                      boxShadow: "var(--shadow-md)",
                    }}
                  >
                    <FiCheckCircle size={17} />
                    {submitting ? "Saving..." : editingClient ? "Update Client Profile" : "Save Client Details"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================== VIEW 2: DIRECTORY TABLE ===================== */}
      {activeTab === "list" && (
        <div className="fade-in">
          {/* Filter / Search Bar */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "16px 20px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "1 1 320px", maxWidth: 520 }}>
              <div style={{ position: "relative", width: "100%" }}>
                <FiSearch
                  size={18}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--slate-400)",
                  }}
                />
                <input
                  type="text"
                  placeholder="Search by client name, focal person, NTN, phone, address..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--slate-300)",
                    fontSize: "0.88rem",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--slate-400)",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Clear search"
                  >
                    <FiX size={15} />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => fetchClients(page, pageSize, debouncedSearch)}
                disabled={loading}
                style={{
                  height: 38,
                  padding: "0 14px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  flexShrink: 0,
                }}
                title="Refresh Clients"
              >
                <FiRefreshCw size={14} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                Refresh
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: "0.85rem", color: "var(--slate-500)", fontWeight: 600 }}>
                Showing {clients.length} of <strong style={{ color: "var(--navy)" }}>{pagination.total}</strong> Clients
                {debouncedSearch && <span style={{ color: "var(--primary)", marginLeft: 4 }}>(filtered)</span>}
              </div>
              <button
                onClick={handleStartAdd}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  backgroundColor: "var(--primary)",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "var(--radius-md)",
                  border: "none",
                  fontWeight: 700,
                  fontSize: "0.84rem",
                  cursor: "pointer",
                }}
              >
                <FiPlus size={16} /> Add Client
              </button>
            </div>
          </div>

          {/* Clients Table */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: "var(--slate-50)",
                      borderBottom: "1px solid var(--slate-200)",
                      textAlign: "left",
                    }}
                  >
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Company Name</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Contact Person</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Email & Phone</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Address</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>NTN #</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 45, textAlign: "center", color: "var(--slate-500)" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
                          <FiRefreshCw size={18} style={{ color: "var(--primary)", animation: "spin 1s linear infinite" }} />
                          <span style={{ fontWeight: 600, fontSize: "0.88rem" }}>Loading clients from server...</span>
                        </div>
                      </td>
                    </tr>
                  ) : clients.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                        <div style={{ marginBottom: 10 }}>No clients found matching your criteria.</div>
                        <button
                          onClick={handleStartAdd}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: "var(--primary-light)",
                            color: "var(--primary)",
                            border: "none",
                            borderRadius: "var(--radius-md)",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                            cursor: "pointer",
                          }}
                        >
                          + Register First Client
                        </button>
                      </td>
                    </tr>
                  ) : (
                    clients.map((c) => (
                      <tr
                        key={c.id}
                        style={{
                          borderBottom: "1px solid var(--slate-100)",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <td style={{ padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: 8,
                                backgroundColor: "#EEF2FF",
                                color: "#4F46E5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <FiUsers size={17} />
                            </div>
                            <div>
                              <span style={{ fontWeight: 700, color: "var(--navy)", display: "block" }}>{c.company_name}</span>
                              {c.status === "inactive" && (
                                <span
                                  style={{
                                    fontSize: "0.72rem",
                                    color: "var(--slate-500)",
                                    backgroundColor: "var(--slate-100)",
                                    padding: "1px 6px",
                                    borderRadius: 4,
                                  }}
                                >
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td style={{ padding: "14px 18px", fontWeight: 600, color: "var(--slate-800)" }}>
                          {c.contact_person_name || <span style={{ color: "var(--slate-400)" }}>—</span>}
                        </td>

                        <td style={{ padding: "14px 18px" }}>
                          {c.email && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--slate-700)" }}>
                              <FiMail size={13} color="var(--slate-400)" />
                              <span>{c.email}</span>
                            </div>
                          )}
                          {c.contact && (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                color: "var(--slate-500)",
                                fontSize: "0.8rem",
                                marginTop: 2,
                              }}
                            >
                              <FiPhone size={13} color="var(--slate-400)" />
                              <span>{c.contact}</span>
                            </div>
                          )}
                          {!c.email && !c.contact && <span style={{ color: "var(--slate-400)" }}>—</span>}
                        </td>

                        <td style={{ padding: "14px 18px", color: "var(--slate-600)", maxWidth: 220 }}>
                          {c.address ? (
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                              <FiMapPin size={13} color="var(--slate-400)" style={{ marginTop: 3, flexShrink: 0 }} />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {c.address}
                              </span>
                            </div>
                          ) : (
                            <span style={{ color: "var(--slate-400)" }}>—</span>
                          )}
                        </td>

                        <td style={{ padding: "14px 18px" }}>
                          {c.ntn ? (
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                padding: "2px 8px",
                                borderRadius: 4,
                                backgroundColor: "var(--slate-100)",
                                color: "var(--slate-800)",
                                fontSize: "0.8rem",
                              }}
                            >
                              {c.ntn}
                            </span>
                          ) : (
                            <span style={{ color: "var(--slate-400)" }}>—</span>
                          )}
                        </td>

                        <td style={{ padding: "14px 18px", textAlign: "right" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <button
                              onClick={() => handleStartEdit(c)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "1px solid var(--slate-200)",
                                backgroundColor: "#fff",
                                color: "var(--slate-700)",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                fontSize: "0.82rem",
                                fontWeight: 600,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "var(--primary-light)";
                                e.currentTarget.style.color = "var(--primary)";
                                e.currentTarget.style.borderColor = "var(--primary)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#fff";
                                e.currentTarget.style.color = "var(--slate-700)";
                                e.currentTarget.style.borderColor = "var(--slate-200)";
                              }}
                            >
                              <FiEdit2 size={14} /> Edit
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => setDeleteModal({ open: true, client: c })}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #FECACA",
                                  backgroundColor: "#FEF2F2",
                                  color: "var(--danger)",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: "0.8rem",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "var(--danger)";
                                  e.currentTarget.style.color = "#fff";
                                  e.currentTarget.style.borderColor = "var(--danger)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#FEF2F2";
                                  e.currentTarget.style.color = "var(--danger)";
                                  e.currentTarget.style.borderColor = "#FECACA";
                                }}
                              >
                                <FiTrash2 size={14} /> Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls for Clients */}
            {pagination.total > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                  padding: "14px 20px",
                  borderTop: "1px solid var(--slate-200)",
                  backgroundColor: "var(--slate-50)",
                  fontSize: "0.84rem",
                  color: "var(--slate-600)",
                }}
              >
                <div>
                  Showing <strong>{pagination.total === 0 ? 0 : (page - 1) * pageSize + 1}</strong> to{" "}
                  <strong>{Math.min((page - 1) * pageSize + clients.length, pagination.total)}</strong> of{" "}
                  <strong>{pagination.total}</strong> {pagination.total === 1 ? "client" : "clients"}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  {/* Rows Per Page Selector */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>Rows:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        const newLimit = Number(e.target.value);
                        setPageSize(newLimit);
                        setPage(1);
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.82rem",
                        backgroundColor: "#fff",
                        fontWeight: 600,
                        color: "var(--navy)",
                        cursor: "pointer",
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  {/* Prev Button */}
                  <button
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: page <= 1 ? "var(--slate-100)" : "#fff",
                      color: page <= 1 ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: page <= 1 || loading ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <FiChevronLeft size={15} /> Prev
                  </button>

                  {/* Numbered Page Buttons */}
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter((p) => {
                        if (pagination.totalPages <= 7) return true;
                        if (p === 1 || p === pagination.totalPages) return true;
                        if (Math.abs(p - page) <= 1) return true;
                        return false;
                      })
                      .map((p, idx, arr) => {
                        const prev = arr[idx - 1];
                        const showEllipsis = prev && p - prev > 1;

                        return (
                          <React.Fragment key={p}>
                            {showEllipsis && (
                              <span style={{ padding: "0 4px", color: "var(--slate-400)" }}>...</span>
                            )}
                            <button
                              type="button"
                              disabled={loading}
                              onClick={() => setPage(p)}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                border: p === page ? "1.5px solid var(--primary)" : "1px solid var(--slate-300)",
                                backgroundColor: p === page ? "var(--primary)" : "#fff",
                                color: p === page ? "#fff" : "var(--slate-700)",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                              }}
                            >
                              {p}
                            </button>
                          </React.Fragment>
                        );
                      })}
                  </div>

                  {/* Next Button */}
                  <button
                    type="button"
                    disabled={page >= pagination.totalPages || loading}
                    onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: "1px solid var(--slate-300)",
                      backgroundColor: page >= pagination.totalPages ? "var(--slate-100)" : "#fff",
                      color: page >= pagination.totalPages ? "var(--slate-400)" : "var(--slate-700)",
                      cursor: page >= pagination.totalPages || loading ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      fontSize: "0.82rem",
                      transition: "all 0.15s ease",
                    }}
                  >
                    Next <FiChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, client: null })} maxWidth={400}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <FiTrash2 size={24} />
          </div>
          <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>
            Delete Client
          </h4>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginBottom: 20 }}>
            Are you sure you want to delete client <strong>"{deleteModal.client?.company_name}"</strong>?
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <button
              type="button"
              onClick={() => setDeleteModal({ open: false, client: null })}
              style={{
                padding: "8px 18px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteClient}
              style={{
                padding: "8px 20px",
                borderRadius: "var(--radius-md)",
                border: "none",
                backgroundColor: "var(--danger)",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Delete Client
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClientsPage;
